import { Link, useSearchParams } from 'react-router-dom';
import { useOrders } from '@/lib/queries';
import { AdminLayout } from '@/components/AdminLayout';
import { StatusBadge, orderStatusTone, humaniseStatus } from '@/components/ui/badge';
import { formatDateTime } from '@/lib/utils';

// Super-admin order history — all locations with a Placed By column.
export function AdminOrderHistory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number.parseInt(searchParams.get('page') ?? '1', 10) || 1;
  const { data, isLoading } = useOrders(page);
  const orders = data?.data ?? [];

  return (
    <AdminLayout>
      <h1 className="page-title">Order History</h1>
      <p className="page-sub mb-6">Purchase orders across all locations.</p>

      <div className="card overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-muted">
              <th className="px-5 py-3 font-medium">Order #</th>
              <th className="px-5 py-3 font-medium">Date Placed</th>
              <th className="px-5 py-3 font-medium">Location</th>
              <th className="px-5 py-3 font-medium">Placed By</th>
              <th className="px-5 py-3 font-medium">Items</th>
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && orders.length === 0 && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-muted">
                  No orders yet.
                </td>
              </tr>
            )}
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-5 py-3 font-mono">
                  <Link to={`/admin/orders/${o.id}`} className="text-brand hover:underline">
                    {o.orderNumber}
                  </Link>
                </td>
                <td className="px-5 py-3 text-ink">{formatDateTime(o.submittedAt)}</td>
                <td className="px-5 py-3 text-ink">{o.locationName}</td>
                <td className="px-5 py-3 text-ink">{o.placedByName}</td>
                <td className="px-5 py-3 text-ink">{o.itemCount}</td>
                <td className="px-5 py-3">
                  <StatusBadge tone={orderStatusTone(o.status)}>{humaniseStatus(o.status)}</StatusBadge>
                </td>
                <td className="px-5 py-3">
                  <Link to={`/admin/orders/${o.id}`} className="text-brand hover:underline">
                    View
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-4 flex items-center gap-2">
          <button
            className="rounded px-3 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40"
            disabled={page <= 1}
            onClick={() => setSearchParams({ page: String(page - 1) })}
          >
            Previous
          </button>
          <span className="text-sm text-muted">
            Page {page} of {data.pagination.totalPages}
          </span>
          <button
            className="rounded px-3 py-1.5 text-sm text-muted hover:bg-surface-2 hover:text-ink disabled:opacity-40"
            disabled={page >= data.pagination.totalPages}
            onClick={() => setSearchParams({ page: String(page + 1) })}
          >
            Next
          </button>
        </div>
      )}
    </AdminLayout>
  );
}
